// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client.js';

/**
 * API-safe view returned by GET
 * /api/organizations/me/eligibility-connection. Mirrors
 * EmrConnectionView from bead 8de but for the clearinghouse side.
 */
export interface EligibilityConnectionView {
  id: string;
  organizationId: string;
  providerKind: 'fixture' | 'changehealthcare' | 'availity';
  displayName: string | null;
  partnerId: string | null;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EligibilityConnectionResponse {
  connection: EligibilityConnectionView | null;
}

export interface UpsertEligibilityConnectionBody {
  providerKind: 'fixture' | 'changehealthcare' | 'availity';
  displayName?: string | null;
  partnerId?: string | null;
  /** Omit to preserve existing credentials when updating. */
  credentials?: { apiKey: string };
}

const QUERY_KEY = ['eligibility-connection'] as const;

export function useEligibilityConnection() {
  return useQuery<EligibilityConnectionResponse>({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiFetch<EligibilityConnectionResponse>('/api/organizations/me/eligibility-connection'),
  });
}

export function useUpsertEligibilityConnection() {
  const qc = useQueryClient();
  return useMutation<EligibilityConnectionResponse, ApiError, UpsertEligibilityConnectionBody>({
    mutationFn: (body) =>
      apiFetch<EligibilityConnectionResponse>('/api/organizations/me/eligibility-connection', {
        method: 'PUT',
        body,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteEligibilityConnection() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, undefined>({
    mutationFn: async () => {
      await apiFetch<undefined>('/api/organizations/me/eligibility-connection', {
        method: 'DELETE',
      });
      return undefined;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
